import React from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold,
  faStrikethrough,
  faLink,
  faItalic,
  faUnderline,
  faQuoteRight,
  faSubscript,
  faSuperscript,
  faListOl,
  faListUl,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faUndo,
  faRedo
} from '@fortawesome/free-solid-svg-icons';
import { Tooltip, Select, Modal, Input, message } from 'antd';

interface ToolbarProps {
  editor: any;
  disable?: boolean;
}

const Toolbar: React.FC<ToolbarProps> = ({ editor, disable = false }) => {
  const [linkModalOpen, setLinkModalOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState<string>('');
  const [messageApi, contextHolder] = message.useMessage();

  const toggleBold = () => editor?.chain().focus().toggleBold().run();
  const toggleStrike = () => editor?.chain().focus().toggleStrike().run();
  const toggleItalic = () => editor?.chain().focus().toggleItalic().run();
  const toggleUnderline = () => editor?.chain().focus().toggleUnderline().run();
  const toggleSubscript = () => editor?.chain().focus().toggleSubscript().run();
  const toggleSuperscript = () => editor?.chain().focus().toggleSuperscript().run();
  const toggleOrderedList = () => editor?.chain().focus().toggleOrderedList().run();
  const toggleBulletList = () => editor?.chain().focus().toggleBulletList().run();
  const toggleBlockquote = () => editor?.chain().focus().toggleBlockquote().run();
  const setTextAlign = (align: 'left' | 'center' | 'right') =>
    editor?.chain().focus().setTextAlign(align).run();
  const toggleLink = () => {
    if (disable) return;
    const { from, to } = editor.state.selection || {};
    const selectedText = from !== to;

    if (!selectedText) {
      messageApi.error('Please select some text first to add or remove a link');
      return;
    }

    const hasLink = editor.isActive('link');

    if (hasLink) {
      Modal.confirm({
        title: 'Link Options',
        content: 'What would you like to do with this link?',
        okText: 'Edit',
        cancelText: 'Remove',
        onOk: () => {
          const currentLink = editor.getAttributes('link');
          if (currentLink && currentLink.href) {
            setLinkUrl(currentLink.href);
          } else {
            setLinkUrl('');
          }
          setLinkModalOpen(true);
        },
        onCancel: () => {
          editor.chain().focus().unsetLink().run();
          messageApi.success('Link removed successfully');
        }
      });
    } else {
      setLinkUrl('');
      setLinkModalOpen(true);
    }
  };

  const handleLinkOk = () => {
    const url = (linkUrl || '').trim();
    editor
      ?.chain()
      .focus()
      .setLink({ href: url || '#' })
      .run();

    setLinkModalOpen(false);
    setLinkUrl('');
    messageApi.success('Link added successfully');
  };

  const handleLinkCancel = () => {
    setLinkModalOpen(false);
  };

  const undo = () => editor?.chain().focus().undo().run();
  const redo = () => editor?.chain().focus().redo().run();

  const setHeading = (level: number) => {
    if (level >= 1 && level <= 3) {
      editor.chain().focus().toggleHeading({ level }).run();
    } else {
      editor.chain().focus().setParagraph().run();
    }
  };

  return (
    <div className="menu">
      {contextHolder}
      <Tooltip title="Undo" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button type="button" onClick={undo} className="menu-button" disabled={disable}>
          <FontAwesomeIcon icon={faUndo} />
        </button>
      </Tooltip>
      <Tooltip title="Redo" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button type="button" onClick={redo} className="menu-button" disabled={disable}>
          <FontAwesomeIcon icon={faRedo} />
        </button>
      </Tooltip>

      <Select
        onChange={(value) => setHeading(Number(value))}
        className="menu-dropdown"
        defaultValue="0"
        style={{
          width: 120
        }}
        disabled={disable}>
        <Select.Option value="0">Paragraph</Select.Option>
        <Select.Option value="1">Heading 1</Select.Option>
        <Select.Option value="2">Heading 2</Select.Option>
        <Select.Option value="3">Heading 3</Select.Option>
      </Select>

      <div
        className="separator"
        style={{ height: '24px', width: '1px', backgroundColor: '#ccc', margin: '0 10px' }}></div>

      <Tooltip title="Bold" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('bold') })}
          onClick={toggleBold}
          disabled={disable}>
          <FontAwesomeIcon icon={faBold} />
        </button>
      </Tooltip>
      <Tooltip title="Italic" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('italic') })}
          onClick={toggleItalic}
          disabled={disable}>
          <FontAwesomeIcon icon={faItalic} />
        </button>
      </Tooltip>
      <Tooltip title="Underline" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('underline') })}
          onClick={toggleUnderline}
          disabled={disable}>
          <FontAwesomeIcon icon={faUnderline} />
        </button>
      </Tooltip>
      <Tooltip title="Strikethrough" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('strike') })}
          onClick={toggleStrike}
          disabled={disable}>
          <FontAwesomeIcon icon={faStrikethrough} />
        </button>
      </Tooltip>
      <Tooltip title="Link" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('link') })}
          onClick={toggleLink}
          disabled={disable}>
          <FontAwesomeIcon icon={faLink} />
        </button>
      </Tooltip>
      <Tooltip title="Subscript" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('subscript') })}
          onClick={toggleSubscript}
          disabled={disable}>
          <FontAwesomeIcon icon={faSubscript} />
        </button>
      </Tooltip>
      <Tooltip title="Superscript" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('superscript') })}
          onClick={toggleSuperscript}
          disabled={disable}>
          <FontAwesomeIcon icon={faSuperscript} />
        </button>
      </Tooltip>

      <div
        className="separator"
        style={{ height: '24px', width: '1px', backgroundColor: '#ccc', margin: '0 10px' }}></div>

      <Tooltip title="Blockquote" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('blockquote') })}
          onClick={toggleBlockquote}
          disabled={true}>
          <FontAwesomeIcon icon={faQuoteRight} />
        </button>
      </Tooltip>

      <div
        className="separator"
        style={{ height: '24px', width: '1px', backgroundColor: '#ccc', margin: '0 10px' }}></div>

      <Tooltip title="Ordered List" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('orderedList') })}
          onClick={toggleOrderedList}
          disabled={disable}>
          <FontAwesomeIcon icon={faListOl} />
        </button>
      </Tooltip>
      <Tooltip title="Bullet List" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('bulletList') })}
          onClick={toggleBulletList}
          disabled={disable}>
          <FontAwesomeIcon icon={faListUl} />
        </button>
      </Tooltip>
      <Tooltip title="Align Left" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('leftAlign') })}
          onClick={() => setTextAlign('left')}
          disabled={disable}>
          <FontAwesomeIcon icon={faAlignLeft} />
        </button>
      </Tooltip>
      <Tooltip title="Align Center" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('centerAlign') })}
          onClick={() => setTextAlign('center')}
          disabled={disable}>
          <FontAwesomeIcon icon={faAlignCenter} />
        </button>
      </Tooltip>
      <Tooltip title="Align Right" placement="bottom" overlayStyle={{ fontSize: '12px' }}>
        <button
          type="button"
          className={classNames('menu-button', { 'is-active': editor?.isActive('rightAlign') })}
          onClick={() => setTextAlign('right')}
          disabled={disable}>
          <FontAwesomeIcon icon={faAlignRight} />
        </button>
      </Tooltip>

      <Modal
        title={editor?.isActive('link') ? 'Edit Link' : 'Add Link'}
        open={linkModalOpen}
        onOk={handleLinkOk}
        onCancel={handleLinkCancel}
        okText="Apply"
        cancelText="Cancel">
        <Input
          placeholder="Enter URL (e.g., https://example.com)"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default Toolbar;
